/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Button, Sheet, SheetProps, Snackbar } from "@mui/joy";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import IconButton from "@mui/joy/IconButton";
import { listItemButtonClasses } from "@mui/joy/ListItemButton";
import Switch from "@mui/joy/Switch";
import Typography from "@mui/joy/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BrandLogo, Chats, LoginButton } from "../components";
import useGoogleLoginStore from "../stores/googleLoginStore";
import useLanguageStore from "../stores/languageStore";
import useNSFWStore from "../stores/nsfwStore";
import { checkPointsAvailability } from "../utils/pointsUtils";
import { getLocalizedPath } from "../utils/urlUtils";
import { AgeVerificationModal } from "./AgeVerificationModal";
import ColorSchemeToggle from "./ColorSchemeToggle";
import { Navigation } from "./navigation";
import {
  resolvePostLoginUserStatus,
  userDataToUserStatus,
} from "../utils/authStatus";

export const closeSidebar = () => {
  const sidebar = document.querySelector(".Sidebar") as HTMLElement;
  if (sidebar) {
    sidebar.style.setProperty("--SideNavigation-slideIn", "0");
    const overlay = document.querySelector(".sidebar-overlay") as HTMLElement;
    if (overlay) {
      overlay.style.opacity = "0";
      overlay.style.pointerEvents = "none";
    }
  }
};

export function Sidebar(props: SidebarProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguageStore();
  const { ...other } = props;
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const isNSFWEnabled = useNSFWStore(
    (state: { isNSFWEnabled: boolean }) => state.isNSFWEnabled,
  );
  const isAgeVerified = useNSFWStore(
    (state: { isAgeVerified: boolean }) => state.isAgeVerified,
  );
  const setNSFWEnabled = useNSFWStore(
    (state: { setNSFWEnabled: (enabled: boolean) => void }) =>
      state.setNSFWEnabled,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setAuthenticated = useGoogleLoginStore(
    (state: any) => state.setAuthenticated,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setGoogleLoginModalVisible = useGoogleLoginStore(
    (state: any) => state.setVisible,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setSuccessCallback = useGoogleLoginStore(
    (state: any) => state.setSuccessCallback,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setReturnCallback = useGoogleLoginStore(
    (state: any) => state.setReturnCallback,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setUserStatus = useGoogleLoginStore(
    (state: any) => state.setUserStatus,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userStatus = useGoogleLoginStore((state: any) => state.userStatus);

  const handleNSFWToggle = () => {
    if (!isAgeVerified) {
      setShowAgeVerification(true);
    } else {
      setNSFWEnabled(!isNSFWEnabled);
      window.location.reload();
    }
  };

  return (
    <>
      <Sheet
        className="Sidebar"
        {...other}
        sx={{
          position: "fixed",
          transform: {
            xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
            md: "none",
          },
          transition: "transform 0.4s, width 0.4s",
          zIndex: 10000,
          height: "100dvh",
          width: "var(--Sidebar-width)",
          top: 0,
          left: 0,
          p: 1.5,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          bgcolor: "background.sidebarBg",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", md: "row" },
            gap: { sm: 0.5, md: 1.5 },
            alignItems: "center",
            py: { sm: 0.25, md: 0.5 },
            borderRadius: "xl",
          }}
        >
          <BrandLogo />
          <Box
            sx={{ display: "flex", gap: { xs: 2, sm: 2, md: 1 }, ml: "auto" }}
          >
            <IconButton
              onClick={closeSidebar}
              aria-label={t("sidebar.closeSidebar")}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                p: { xs: 0.5, md: 1 },
              }}
            >
              <CloseRoundedIcon
                sx={{ fontSize: { xs: "1.25rem", md: "1.5rem" } }}
              />
            </IconButton>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mb: 2,
            "& .MuiListItemButton-root": {
              "& .MuiTypography-root": {
                fontSize: "1rem",
              },
            },
          }}
        >
          <Navigation />
          <Button
            variant="solid"
            color="primary"
            startDecorator={<AddCircleOutlineRoundedIcon />}
            sx={{
              "--Button-gap": "8px",
              mx: 1,
              fontWeight: 600,
              fontSize: "1rem",
              borderRadius: "xl",
              display: userStatus?.isAuthenticated
                ? { xs: "none", sm: "none", md: "flex" }
                : "flex",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 2px 8px rgba(0, 0, 0, 0.5)"
                  : "sm",
              minHeight: "44px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 4px 12px rgba(0, 0, 0, 0.8)"
                    : "md",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "#7B1FA2" : "#AB47BC",
              },
            }}
            onClick={async () => {
              closeSidebar();
              const goToCreatorCenter = async () => {
                navigate(getLocalizedPath("/creator-center", currentLanguage));
              };
              if (!userStatus?.isAuthenticated) {
                setGoogleLoginModalVisible(true);
                setSuccessCallback(() => {
                  goToCreatorCenter();
                });
                setReturnCallback(() => {});
                return;
              }
              goToCreatorCenter();
            }}
          >
            {t("sidebar.createCharacter")}
          </Button>
        </Box>

        <Box
          sx={{
            bgcolor: "background.level1",
            borderRadius: "xl",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 1.5,
            gap: 1,
            maxWidth: "280px",
            overflow: "auto",
            [`& .${listItemButtonClasses.root}`]: {
              gap: 1.5,
              borderRadius: "lg",
            },
            "& .LoginButton": {
              border: "none",
              bgcolor: "background.surface",
              "&:hover": {
                bgcolor: "background.level3",
                borderColor: "transparent",
                borderWidth: 0,
              },
            },
          }}
        >
          {userStatus?.isAuthenticated ? (
            <Box sx={{ width: "100%", alignItems: "left", pl: 1, pr: 1 }}>
              <Chats />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                mt: 1,
              }}
            >
              <LoginButton
                signInMethod="google.com"
                showRecommendedBadge={false}
                onLoginSuccess={async (userData) => {
                  try {
                    const optimistic = userDataToUserStatus(userData);
                    const userStatus =
                      await resolvePostLoginUserStatus(optimistic);
                    setAuthenticated(true);
                    setUserStatus(userStatus);
                  } catch {
                    // Ignore auth errors
                  }
                }}
              />
              <Typography
                level="body-xs"
                sx={{
                  textAlign: "center",
                  color: "neutral.500",
                  fontSize: "0.875rem",
                }}
              >
                {t("sidebar.loginToSaveChats")}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            bgcolor: "background.level1",
            p: 1.5,
            borderRadius: "xl",
          }}
        >
          <Avatar
            variant="soft"
            src={userStatus?.user?.avatar || undefined}
            alt={`${userStatus?.user?.username || t("sidebar.guest")}'s profile picture`}
            sx={{
              width: 32,
              height: 32,
              cursor: userStatus?.isAuthenticated ? "pointer" : "default",
              transition: "transform 0.2s, opacity 0.2s",
              bgcolor: "neutral.400",
              border: "none",
              "&:hover": userStatus?.isAuthenticated
                ? {
                    opacity: 0.85,
                    transform: "scale(1.05)",
                    boxShadow: "sm",
                  }
                : {},
            }}
            onClick={() => {
              if (userStatus?.isAuthenticated) {
                navigate("/account");
                closeSidebar();
              }
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              level="title-sm"
              sx={{
                cursor: userStatus?.isAuthenticated ? "pointer" : "default",
                transition: "color 0.2s",
                fontSize: "0.875rem",
                "&:hover": userStatus?.isAuthenticated
                  ? {
                      color: "primary.main",
                      textDecoration: "underline",
                    }
                  : {},
              }}
              onClick={() => {
                if (userStatus?.isAuthenticated) {
                  navigate("/account");
                  closeSidebar();
                }
              }}
            >
              {userStatus?.user?.username || t("sidebar.guest")}
            </Typography>
            <Typography level="body-xs" sx={{ fontSize: "0.875rem" }}>
              {userStatus?.user?.email || t("sidebar.freePlan")}
            </Typography>
          </Box>
        </Box>
        {userStatus?.isAuthenticated && (
          <Box
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              mx: 1,
              display: "flex",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography level="body-sm" sx={{ fontSize: "0.75rem" }}>
                {t("sidebar.darkMode")}
              </Typography>
              <ColorSchemeToggle />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                level="body-sm"
                component="label"
                htmlFor="spicy-mode-switch"
                sx={{ fontSize: "0.75rem" }}
              >
                {t("sidebar.spicy")}
              </Typography>
              <Switch
                id="spicy-mode-switch"
                checked={isNSFWEnabled}
                onChange={handleNSFWToggle}
                color={isNSFWEnabled ? "danger" : "neutral"}
                aria-label={t("sidebar.toggleSpicyMode")}
              />
            </Box>
          </Box>
        )}
      </Sheet>
      <Snackbar
        variant="solid"
        color="danger"
        size="lg"
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        sx={{ maxWidth: 400 }}
      >
        {errorMessage}
      </Snackbar>
      <AgeVerificationModal
        open={showAgeVerification}
        onClose={() => setShowAgeVerification(false)}
      />
      {/* Add overlay for mobile */}
      <Box
        className="sidebar-overlay"
        onClick={() => {
          closeSidebar();
        }}
        sx={{
          position: "fixed",
          zIndex: 9999,
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          bgcolor: "rgba(0, 0, 0, 0.5)",
          transition: "opacity 0.4s",
          opacity: { xs: 0, md: 0 },
          pointerEvents: {
            xs: "none",
            md: "none",
          },
          display: { xs: "block", md: "none" },
        }}
      />
    </>
  );
}

export interface SidebarProps extends Omit<SheetProps, "children"> {
  onClose?: () => void;
  open?: boolean;
}
